import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { IconName as GitBookIconName } from '@gitbook/icons';
import { Icon } from '@gitbook/spine-icons';
import {
    ChatDisclosure,
    ChatLine,
    ChatLineStatus,
    type ChatLineStatusKind,
    type IconDefinition,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { NavigationLink } from '../../navigation';
import { SiteSetupStepCard } from '../AgentSiteSetup';
import { useOptionalContentReferenceContext } from '../ContentReferences';
import { ContentIcon } from '../Icons';
import { SpaceLinkSuspense } from '../Space';
import { useBackgroundAgent } from './BackgroundAgentsContext';
import { ImportRunCard, OpenAPIImportCard } from './ImportRunCard';
import { useEditTargetDetails } from './useEditTargetDetails';

const LIVE_TOOLS: ReadonlySet<api.AIToolCall['tool']> = new Set<api.AIToolCall['tool']>([
    'startBackgroundAgent',
    'startContentImport',
    'importOpenAPISpec',
    'siteSetupStep',
    'mergeChangeRequest',
]);

/**
 * The tool calls of a step, one line each — or, for several calls of the same tool, one line
 * summarizing them that opens onto the rest.
 */
export function ToolCallsSummary(props: { toolCalls: api.AIToolCall[]; loading: boolean }) {
    const { toolCalls, loading } = props;

    const groups = React.useMemo(() => {
        /**
         * Filter unrecognised tools so they don't display in the summary,
         * rather than throwing an error and making the chat UI unusable.
         * One reason this can happen is version mismatch between client and server.
         */
        const recognisedToolCalls = getRecognisedToolCalls(toolCalls);
        return groupToolCalls(recognisedToolCalls);
    }, [toolCalls]);

    return (
        <>
            {groups.map((group, index) => (
                <ToolCallsGroup key={index} toolCalls={group} loading={loading} />
            ))}
        </>
    );
}

/**
 * A call kept in view rather than folded into the work line: one that starts something reporting
 * back live (a background agent, an import), or a step of the conversation (a setup step, a merge).
 */
export function isLiveToolCall(toolCall: api.AIToolCall): boolean {
    return LIVE_TOOLS.has(toolCall.tool);
}

function getRecognisedToolCalls(toolCalls: api.AIToolCall[]): api.AIToolCall[] {
    return toolCalls.filter((toolCall) => Object.keys(TOOLS).includes(toolCall.tool));
}

function ToolCallsGroup(props: { toolCalls: api.AIToolCall[]; loading: boolean }) {
    const { toolCalls, loading = false } = props;
    const firstToolCall = toolCalls[0];
    assertIsDefined(firstToolCall);

    const groupLabel = getGroupDescriptionForToolCall({ toolCalls, loading });
    if (toolCalls.length === 1 || !groupLabel) {
        return (
            <>
                {toolCalls.map((toolCall, index) => (
                    <ToolCallLine key={index} toolCall={toolCall} loading={loading} />
                ))}
            </>
        );
    }

    return (
        <ChatDisclosure
            icon={getIconForToolCall({ toolCall: firstToolCall, loading })}
            label={groupLabel}
            loading={loading}
        >
            {toolCalls.map((toolCall, index) => (
                <ToolCallLine key={index} toolCall={toolCall} loading={loading} nested />
            ))}
        </ChatDisclosure>
    );
}

/**
 * One tool call as a line. `nested` for a line under a group's summary, which has said what the
 * tool was: the line then carries no icon of its own.
 */
function ToolCallLine(props: { toolCall: api.AIToolCall; loading: boolean; nested?: boolean }) {
    const { toolCall, loading, nested = false } = props;

    // Not a record of something done, like the others, but of something still going on elsewhere:
    // the line carries its live status, and opens onto it.
    if (toolCall.tool === 'startBackgroundAgent') {
        return <BackgroundAgentLine toolCall={toolCall} nested={nested} />;
    }

    if (toolCall.tool === 'siteSetupStep') {
        return <SiteSetupStepCard toolCall={toolCall} />;
    }

    const line = (
        <ChatLine
            icon={nested ? undefined : getIconForToolCall({ toolCall, loading })}
            loading={loading}
        >
            {getDescriptionForToolCall({ toolCall, loading })}
        </ChatLine>
    );

    // Likewise an import, which runs on long after the call that started it.
    if (toolCall.tool === 'startContentImport' && !loading) {
        return (
            <ImportRunCard
                toolCall={toolCall}
                source={toolCall.url ?? formatImportedFiles(toolCall.files)}
                fallback={line}
            />
        );
    }

    if (toolCall.tool === 'importOpenAPISpec' && !loading) {
        return <OpenAPIImportCard toolCall={toolCall} />;
    }

    return line;
}

function getIconForToolCall<ToolType extends api.AIToolCall['tool']>(input: {
    toolCall: Extract<api.AIToolCall, { tool: ToolType }>;
    loading: boolean;
}) {
    return TOOLS[input.toolCall.tool].icon({
        toolCall: input.toolCall,
        loading: input.loading,
    });
}

function getDescriptionForToolCall<ToolType extends api.AIToolCall['tool']>(input: {
    toolCall: Extract<api.AIToolCall, { tool: ToolType }>;
    loading: boolean;
}) {
    return TOOLS[input.toolCall.tool].description({
        toolCall: input.toolCall,
        loading: input.loading,
    });
}

function getGroupDescriptionForToolCall<ToolType extends api.AIToolCall['tool']>(input: {
    toolCalls: Extract<api.AIToolCall, { tool: ToolType }>[];
    loading: boolean;
}) {
    return TOOLS[input.toolCalls[0].tool].groupDescription({
        toolCalls: input.toolCalls,
        loading: input.loading,
    });
}

const TOOLS: {
    [toolType in api.AIToolCall['tool']]: {
        /**
         * Icon to display for the tool call.
         */
        icon: (input: {
            toolCall: Extract<api.AIToolCall, { tool: toolType }>;
            loading: boolean;
        }) => IconDefinition;

        /**
         * Get a description for the tool call.
         */
        description: (input: {
            toolCall: Extract<api.AIToolCall, { tool: toolType }>;
            loading: boolean;
        }) => React.ReactNode;

        /**
         * Get a description for a group of tool calls.
         */
        groupDescription: (input: {
            toolCalls: Extract<api.AIToolCall, { tool: toolType }>[];
            loading: boolean;
        }) => string | null;
    };
} = {
    getPageContent: {
        icon: () => Icon.Page,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Reading' : 'Read'} ${toolCalls.length} pages`,
    },
    getReusableContent: {
        icon: () => Icon.ReusableContent,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'} reusable content{' '}
                <strong>{toolCall.reusableContent.title}</strong>
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Reading' : 'Read'} ${toolCalls.length} reusable contents`,
    },
    search: {
        icon: () => Icon.SearchOrAsk,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Searching' : 'Searched'} <strong>{toolCall.query}</strong>
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Searching' : 'Searched'} ${toolCalls.length} queries`,
    },
    getPages: {
        icon: () => Icon.AddPageGroup,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Listing' : 'Listed'} the pages
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    getReusableContents: {
        icon: () => Icon.ReusableContent,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Listing' : 'Listed'} the reusable contents
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    getRecordContent: {
        icon: () => Icon.Connectors,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'} record <strong>{toolCall.title}</strong>
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Reading' : 'Read'} ${toolCalls.length} records`,
    },
    getVariables: {
        icon: () => Icon.Variable,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Listing' : 'Listed'} the variables
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    updateVariables: {
        icon: () => Icon.Variable,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Updating' : 'Updated'} variables
                {toolCall.page ? (
                    <>
                        {' '}
                        on <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                    </>
                ) : null}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Updating' : 'Updated'} variables ${toolCalls.length} times`,
    },
    getTags: {
        icon: () => Icon.Tag,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Listing' : 'Listed'} the tags
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    updateTags: {
        icon: () => Icon.Tag,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Updating' : 'Updated'} tags
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Updating' : 'Updated'} tags ${toolCalls.length} times`,
    },
    editPage: {
        icon: () => Icon.Edit,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Editing' : 'Edited'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) => {
            const pageCount = new Set(toolCalls.map((toolCall) => toolCall.page.id)).size;
            return `${loading ? 'Editing' : 'Edited'} ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`;
        },
    },
    createPage: {
        icon: () => Icon.Page,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Creating' : 'Created'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Creating' : 'Created'} ${toolCalls.length} pages`,
    },
    deletePage: {
        icon: () => Icon.Edit,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Deleting' : 'Deleted'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Deleting' : 'Deleted'} ${toolCalls.length} pages`,
    },
    renamePage: {
        icon: () => Icon.Edit,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Renaming' : 'Renamed'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} /> to{' '}
                <strong>{toolCall.title}</strong>
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Renaming' : 'Renamed'} ${toolCalls.length} pages`,
    },
    movePage: {
        icon: () => Icon.PageDuplicate,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Moving' : 'Moved'}{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                {toolCall.moveType === 'before' && toolCall.to ? (
                    <>
                        {' '}
                        before <PageLink spaceId={toolCall.spaceId} page={toolCall.to} />
                    </>
                ) : toolCall.moveType === 'after' && toolCall.to ? (
                    <>
                        {' '}
                        after <PageLink spaceId={toolCall.spaceId} page={toolCall.to} />
                    </>
                ) : toolCall.moveType === 'within' && toolCall.to ? (
                    <>
                        {' '}
                        into <PageLink spaceId={toolCall.spaceId} page={toolCall.to} />
                    </>
                ) : null}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Moving' : 'Moved'} ${toolCalls.length} pages`,
    },
    listModifiedPages: {
        icon: () => Icon.Page,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Listing' : 'Listed'} the modified pages
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    getPageDiff: {
        icon: () => Icon.Page,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'} the diff for page{' '}
                <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Reading' : 'Read'} the diff for ${toolCalls.length} pages`,
    },
    createReusableContent: {
        icon: () => Icon.ReusableContent,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Creating' : 'Created'} reusable content{' '}
                <strong>{toolCall.reusableContent.title}</strong>
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Creating' : 'Created'} ${toolCalls.length} reusable contents`,
    },
    editReusableContent: {
        icon: () => Icon.Edit,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Editing' : 'Edited'} reusable content{' '}
                <strong>{toolCall.reusableContent.title}</strong>
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Editing' : 'Edited'} ${toolCalls.length} reusable contents`,
    },
    createChangeRequest: {
        icon: () => Icon.ChangeRequestDraft,
        description: ({ toolCall, loading }) => (
            <>
                {toolCall.action === 'create'
                    ? loading
                        ? 'Creating a'
                        : 'Created a'
                    : loading
                      ? 'Updating'
                      : 'Updated'}{' '}
                change request{' '}
                <ChangeRequestLink
                    spaceId={toolCall.spaceId}
                    number={toolCall.changeRequest.number}
                    subject={toolCall.changeRequest.subject}
                />
            </>
        ),
        groupDescription: () => null,
    },
    getChangeRequestDetails: {
        icon: () => Icon.Eye,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'} the change request details
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    startContentImport: {
        icon: () => Icon.Download,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Importing' : 'Started importing'} content from{' '}
                {toolCall.url ?? formatImportedFiles(toolCall.files)}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    importOpenAPISpec: {
        icon: () => Icon.Download,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Importing' : 'Imported'} the API reference from{' '}
                {toolCall.url ?? toolCall.file ?? toolCall.specSlug}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    siteSetupStep: {
        // Drawn by {@link SiteSetupStepCard}; this is what a group of calls would read.
        icon: () => Icon.Check,
        description: ({ toolCall }) => <>Setup step {toolCall.step}</>,
        groupDescription: () => null,
    },
    startBackgroundAgent: {
        icon: () => <ContentIcon icon="robot" />,
        // Drawn by {@link BackgroundAgentLine}, which needs the agent's live state; this is what a
        // surface with none of it would say.
        description: ({ toolCall }) => <>Background agent: {toolCall.title}</>,
        groupDescription: ({ toolCalls }) =>
            toolCalls.length > 1 ? `Started ${toolCalls.length} background agents` : null,
    },
    checkContentImport: {
        icon: () => Icon.Download,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Checking' : 'Checked'} the import
                {toolCall.totalPages > 0
                    ? ` — ${toolCall.importedPages} of ${toolCall.totalPages} pages`
                    : null}
            </>
        ),
        groupDescription: () => null,
    },
    setChangeRequestDetails: {
        icon: () => Icon.Pencil,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Updating' : 'Updated'} the change request title and description
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    archiveChangeRequest: {
        icon: () => Icon.ChangeRequestArchive,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Archiving' : 'Archived'} the change request: {toolCall.reason}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    mergeChangeRequest: {
        icon: () => Icon.ChangeRequestMerged,
        description: ({ toolCall }) => <MergedChangeRequest toolCall={toolCall} />,
        groupDescription: () => null,
    },
    comment: {
        icon: () => Icon.Comment,
        description: ({ toolCall, loading }) => {
            if (!toolCall.page) {
                return (
                    <>
                        {loading ? 'Posting' : 'Posted'} a comment
                        <OtherSpaceLink spaceId={toolCall.spaceId} />
                    </>
                );
            }
            return (
                <>
                    {loading ? 'Posting' : 'Posted'}{' '}
                    <NavigationLink
                        to={{
                            current: {
                                page: toolCall.page.path ?? '',
                                sideSheet: {
                                    type: 'comments',
                                    comment: toolCall.commentId,
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        a comment
                    </NavigationLink>{' '}
                    on <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        },
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Posting' : 'Posted'} ${toolCalls.length} comments`,
    },
    replyToComment: {
        icon: () => Icon.Comment,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Posting' : 'Posted'}{' '}
                <NavigationLink
                    to={{
                        current: {
                            page: toolCall.page?.path ?? '',
                            sideSheet: {
                                type: 'comments',
                                comment: toolCall.commentId,
                            },
                        },
                    }}
                    className="text-inherit underline decoration-dashed"
                >
                    a reply
                </NavigationLink>
                {toolCall.page ? (
                    <>
                        {' '}
                        on <PageLink spaceId={toolCall.spaceId} page={toolCall.page} />
                    </>
                ) : null}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: ({ toolCalls, loading }) =>
            `${loading ? 'Posting' : 'Posted'} ${toolCalls.length} replies`,
    },
    getComments: {
        icon: () => Icon.Comment,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Reading' : 'Read'} {toolCall.commentId ? 'a comment' : 'the comments'}
                <OtherSpaceLink spaceId={toolCall.spaceId} />
            </>
        ),
        groupDescription: () => null,
    },
    submitChangeRequestReview: {
        icon: () => Icon.ChangeRequestOpen,
        description: ({ toolCall, loading }) => {
            const decision = toolCall.status === 'approved' ? 'Approved' : 'Changes requested';
            return (
                <>
                    {loading ? 'Submitting' : 'Submitted'}{' '}
                    <NavigationLink
                        to={{
                            current: {
                                sideSheet: {
                                    type: 'comments',
                                    comment: toolCall.commentId,
                                },
                            },
                        }}
                        className="text-inherit underline decoration-dashed"
                    >
                        a review
                    </NavigationLink>{' '}
                    ({decision})
                    <OtherSpaceLink spaceId={toolCall.spaceId} />
                </>
            );
        },
        groupDescription: () => null,
    },
    mcp: {
        icon: () => Icon.MCP,
        description: ({ toolCall, loading }) =>
            // A server that told us what it is doing says it in the user's words ("Searching
            // documentation"); one that did not can only be reported by the tool it ran.
            toolCall.mcpToolTitle ? (
                <>{toolCall.mcpToolTitle}</>
            ) : (
                <>
                    {loading ? 'Calling' : 'Called'} <strong>{toolCall.mcpToolName}</strong>
                </>
            ),
        groupDescription: () => null,
    },
    other: {
        icon: ({ toolCall }) => <ContentIcon icon={toolCall.summary.icon as GitBookIconName} />,
        description: ({ toolCall, loading }) => (
            <>
                {loading ? 'Running: ' : ''}
                {toolCall.summary.text}
            </>
        ),
        groupDescription: () => null,
    },
};

/**
 * Name the files an import read, falling back to a count when there are too many to list.
 */
function formatImportedFiles(files: string[] | undefined): string {
    if (!files || files.length === 0) {
        return 'the attached files';
    }
    if (files.length > 3) {
        return `${files.length} attached files`;
    }
    return files.join(', ');
}

function groupToolCalls(toolCalls: api.AIToolCall[]): api.AIToolCall[][] {
    const byTool = new Map<api.AIToolCall['tool'], api.AIToolCall[]>();
    for (const toolCall of toolCalls) {
        const group = byTool.get(toolCall.tool);
        if (group) {
            group.push(toolCall);
        } else {
            byTool.set(toolCall.tool, [toolCall]);
        }
    }
    return Array.from(byTool.values());
}

function PageLink(props: { spaceId: string; page: api.ChangedRevisionPage }) {
    const { spaceId, page } = props;

    return (
        <NavigationLink
            to={{
                current: {
                    type: 'space',
                    space: spaceId,
                    mode: 'edit',
                    page: page.path ?? '',
                },
            }}
            className="text-inherit underline decoration-dashed"
        >
            {page.title}
        </NavigationLink>
    );
}

function ChangeRequestLink(props: { spaceId: string; number: number; subject: string }) {
    const { spaceId, number, subject } = props;

    return (
        <NavigationLink
            to={{
                screen: { type: 'change_requests' },
                overlay: {
                    type: 'space',
                    space: spaceId,
                    changeRequest: number,
                    mode: undefined,
                },
            }}
            className="text-inherit underline decoration-dotted"
        >
            "{subject}" (#{number})
        </NavigationLink>
    );
}

/**
 * The change request the agent merged, as a link to open it once its number is known.
 */
function MergedChangeRequest(props: {
    toolCall: Extract<api.AIToolCall, { tool: 'mergeChangeRequest' }>;
}) {
    const { toolCall } = props;
    const { label, number } = useEditTargetDetails({
        kind: 'change_request',
        spaceId: toolCall.spaceId,
        changeRequest: toolCall.changeRequestId,
    });

    if (number === undefined) {
        return <>Merged the change request</>;
    }

    return (
        <>
            Merged change request{' '}
            <ChangeRequestLink spaceId={toolCall.spaceId} number={number} subject={label} />
        </>
    );
}

/**
 * Link to a space that is not the current space.
 */
function OtherSpaceLink(props: { spaceId: string; prefix?: React.ReactNode }) {
    const { spaceId, prefix = ' in ' } = props;
    // Optional: in the agent dock there is no current space, so every space is "another space".
    const context = useOptionalContentReferenceContext();

    if (context?.space === spaceId) {
        return null;
    }

    return (
        <>
            {prefix}
            <SpaceLinkSuspense spaceId={spaceId} />
        </>
    );
}

/**
 * One background agent the conversation started: what it was asked to do, how far it has got, and
 * — the whole line — a way into its own conversation.
 *
 * The tool call is a message and never changes, so the status comes from the agent's live state —
 * a transcript rendered without it (an older conversation, or a surface with no state to hand)
 * still names the task, and one with nowhere to open the conversation still reports it.
 */
function BackgroundAgentLine(props: {
    toolCall: Extract<api.AIToolCall, { tool: 'startBackgroundAgent' }>;
    /** Under a summary that has said these are background agents: the title is enough. */
    nested: boolean;
}) {
    const { toolCall, nested } = props;
    const { agent, onOpen } = useBackgroundAgent(toolCall.agentId);
    const status = agent ? BACKGROUND_AGENT_STATUS[agent.status] : null;

    return (
        <ChatLine
            icon={nested ? undefined : <ContentIcon icon="robot" />}
            onPress={onOpen}
            trailing={
                status || onOpen ? (
                    <>
                        {status ? (
                            <ChatLineStatus kind={status.kind}>{status.label}</ChatLineStatus>
                        ) : null}
                        {onOpen ? <Icon.ArrowUpRight className="size-3" /> : null}
                    </>
                ) : undefined
            }
        >
            {nested ? toolCall.title : <>Background agent: {toolCall.title}</>}
        </ChatLine>
    );
}

const BACKGROUND_AGENT_STATUS: Record<
    api.AgentBackgroundAgentStatus,
    { label: string; kind: ChatLineStatusKind }
> = {
    running: { label: 'Working', kind: 'working' },
    completed: { label: 'Done', kind: 'success' },
    failed: { label: 'Failed', kind: 'danger' },
    stopped: { label: 'Stopped', kind: 'neutral' },
};
